import { useTranslation } from 'react-i18next';

import type { SupportTotals } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

/** Chat-assisted revenue against the store's gross for the same days. */
export function AttributionCard({ totals }: { totals: SupportTotals | undefined }) {
  const { t } = useTranslation('reports');
  const format = useFormat();
  const share = totals?.attributedShare ?? 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-[15px]">{t('support.attribution.title')}</CardTitle>
        <CardDescription>{t('support.attribution.description')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {totals ? (
          <>
            <div
              className="bg-muted flex h-2.5 overflow-hidden rounded-full"
              role="img"
              aria-label={t('support.attribution.shareSummary', {
                share: format.percent(share, { maximumFractionDigits: 1 }),
              })}
            >
              <div
                className="bg-chart-1 h-full"
                style={{ width: `${Math.min(share, 1) * 100}%` }}
              />
            </div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
              <dt className="text-muted-foreground">{t('support.attribution.orders')}</dt>
              <dd className="text-right tabular-nums">{format.number(totals.attributedOrders)}</dd>
              <dt className="text-muted-foreground">{t('support.attribution.revenue')}</dt>
              <dd className="text-right tabular-nums">{format.money(totals.attributedRevenue)}</dd>
              <dt className="text-muted-foreground">{t('support.attribution.refunds')}</dt>
              <dd className="text-right tabular-nums">{format.money(totals.attributedRefunds)}</dd>
              <dt className="font-medium">{t('support.attribution.net')}</dt>
              <dd className="text-right font-medium tabular-nums">
                {format.money(totals.attributedNet)}
              </dd>
              <dt className="text-muted-foreground">{t('support.attribution.storeRevenue')}</dt>
              <dd className="text-right tabular-nums">{format.money(totals.storeRevenue)}</dd>
              <dt className="text-muted-foreground">{t('support.attribution.share')}</dt>
              <dd className="text-right tabular-nums">
                {totals.attributedShare === null
                  ? '—'
                  : format.percent(totals.attributedShare, { maximumFractionDigits: 1 })}
              </dd>
            </dl>
          </>
        ) : (
          <Skeleton className="h-40" />
        )}
      </CardContent>
    </Card>
  );
}
